import actionTypes from 'src/modules/shared/context/actionTypes';
import api from 'src/utils/api';
import { setAlert } from 'src/modules/shared/alert/actions';
import { showCreateAccountSocialModal, showExistingLoginSocialModal, showInvalidLoginModal,
  closeModal }  from 'src/modules/shared/modal/actions';
import { trackEvent, trackFootfallEvent, trackEventForPartner } from 'src/utils/analytics';
import { getBranch } from 'src/utils/cookie';
import footfallEventTypes from 'src/constants/footfallEventTypes';
import { loadJoinCommunityPage } from 'src/utils/router';
import { enrollForProgram } from 'src/modules/programs/overview/actions';
import { followCommunity } from 'src/modules/community/shared/actions';
import signupContextTypes from 'src/constants/signupContext';
import { respondPost } from 'src/modules/community/singlePost/actions/actions';
import { showResearchOptInModal, showHomeTopTip } from 'src/modules/shared/modal/actions';
import { scrollToPostId } from 'src/utils/scroll';
import { isIE } from 'src/utils/browser';
import * as log from 'loglevel';

const PROGRAM_MODULE_ELEMENTS = [
  signupContextTypes.element.PROGRAM_SECTION_MARK_AS_COMPLETE,
  signupContextTypes.element.PROGRAM_SECTION_TOGGLE_SECTION,
  signupContextTypes.element.PROGRAM_MODULE_PREVIOUS_LINK,
  signupContextTypes.element.PROGRAM_MODULE_NEXT_LINK,
];

const STATUS_CODES = {
  NO_ACCOUNT: '40400010',
  EXISTING_ACCOUNT: '40400012',
  INVALID: '40400013',
  WRONG_DETAILS: '40011221',
};

export const setServerSession = (sessionId) => {
  return {
    type: actionTypes.SET_SERVER_SESSION,
    sessionId,
  };
};

export const unsetServerSession = () => {
  return {
    type: actionTypes.UNSET_SERVER_SESSION,
  };
};

export const login = ({ password, username, staySignedIn = true }) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.LOGIN });
    try {
      const response = await api.auroraPost('session', { password, username, staySignedIn });
      const { data: { payload, status, meta } } = response;
      if (status && !status.success) {
        dispatch(setAlert(status.message, 'danger', false));
        return dispatch({ type: actionTypes.LOGIN_FAILURE });
      }
      const { user } = payload;
      dispatch(closeModal());
      await dispatch({ type: actionTypes.LOGIN_SUCCESS, user, lang: meta.locale.split('_')[0] });

      const { pages: { singlePost: { visitorReplyData } } } = getState();
      if (visitorReplyData) {
        dispatch(respondPost(visitorReplyData));
      }
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.LOGIN_FAILURE });
    }
  };
};

export const loginWithSocial = (socialLoginData, socialType, signupContext, onSuccess) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.SOCIAL_LOGIN });
    const { data: { status, payload } } = await api.auroraPost('session/oauth', socialLoginData);
    try {
      const onFinish = async () => {
        const { pages: { singlePost: { visitorReplyData } } } = getState();
        if (visitorReplyData) {
          await dispatch(respondPost(visitorReplyData));
        }
        onSuccess();
      };
      if (status.success) {
        dispatch(closeModal());
        trackEvent('Account', `Logged in via ${socialType}`);
        const { user } = payload;
        await dispatch({ type: actionTypes.SOCIAL_LOGIN_SUCCESS, user });
        onFinish();
      } else {
        handleApiSocialLoginResponse(status, socialLoginData, dispatch, signupContext, onFinish);
      }
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.SOCIAL_LOGIN_FAILURE });
    }
  };
};

const handleApiSocialLoginResponse = (status, authData, dispatch, signupContext, onSuccess) => {
  switch (status.responseCode) {
    case STATUS_CODES.NO_ACCOUNT:
      return dispatch(showCreateAccountSocialModal(authData, signupContext, onSuccess));
    case STATUS_CODES.EXISTING_ACCOUNT:
      return dispatch(showExistingLoginSocialModal(authData, onSuccess));
    case STATUS_CODES.INVALID:
      return dispatch(showInvalidLoginModal());
    case STATUS_CODES.WRONG_DETAILS:
      return dispatch(setAlert(status.message, 'danger', false));
  }
};

const handleSignupFlow = (dispatch, getState, signupContext, userId, visitorReplyId) => {
  // if the user clicks on "Join program", we don't want to direct them
  if (signupContext && signupContext.element === signupContextTypes.element.ENROLL_PROGRAM) {
    return;
  }
  // if the user clicks on any element in the program module, we don't want to direct them
  if (signupContext && PROGRAM_MODULE_ELEMENTS.indexOf(signupContext.element) !== -1) {
    return;
  }

  if (visitorReplyId) {
    setTimeout(() => {
      dispatch(showResearchOptInModal({
        onFinish: () => {
          if (isIE()) {
            scrollToPostId(visitorReplyId);
          } else {
            dispatch(showHomeTopTip({
              onClose: () => scrollToPostId(visitorReplyId),
            }));
          }
        },
      }));
    }, 1000);
    return;
  }

  loadJoinCommunityPage();
};

export const signup = ({ password, username, email, accessToken, isVerified }, signupContext = {}) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.SIGN_UP });

    try {
      const { context: { currentCommunity }, pages: { singlePost: { visitorReplyData } } } = getState();
      const response = await api.auroraPost('profile', { password, username, email, accessToken, isVerified });
      const { data: { status, payload: { user } } } = response;

      if (status && !status.success) {
        dispatch(setAlert(status.message, 'danger', false));
        return dispatch({ type: actionTypes.SIGN_UP_FAILURE });
      }

      await dispatch({ type: actionTypes.SIGN_UP_SUCCESS, signupContext });

      if (!!currentCommunity) {
        await dispatch(followCommunity(currentCommunity, user.id, null, true));
      }

      let visitorReplyId = null;
      if (visitorReplyData) {
        const response = await dispatch(respondPost(visitorReplyData));
        visitorReplyId = response.postId;
      }

      setTimeout(() => {
        dispatch(closeModal());
        handleSignupFlow(dispatch, getState, signupContext, user.id, visitorReplyId);
      }, 100);
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.SIGN_UP_FAILURE });
    }
  };
};

export const signupWithSocial = (socialSignupData, socialType, signupContext = {}) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.SOCIAL_SIGN_UP });
    try {
      const { context: { currentCommunity }, pages: { singlePost: { visitorReplyData } } } = getState();
      const response = await api.auroraPost('profile', socialSignupData);
      const { data: { payload, status } } = response;

      if (status && !status.success) {
        throw new Error(status.message, status.message);
      }

      const user = payload.user;

      const signupTypes = {
        'Facebook': 'FB',
        'Google': 'GO',
        'default': 'HU',
      };
      const signupType = signupTypes[socialType] || signupTypes.default;
      const branch = getBranch();
      const signupAction = `${branch};${signupType};COMMUNITY;`;
      const signupUrl = window.location.pathname;

      trackEvent('NewAccount', signupAction, signupUrl);
      trackFootfallEvent(footfallEventTypes.SIGNED_UP);
      trackEvent('Account', `Created ${socialType} (${branch})`);

      await dispatch({ type: actionTypes.SOCIAL_SIGN_UP_SUCCESS });

      if (!!currentCommunity) {
        // tracking for partner
        trackEventForPartner(currentCommunity, 'Account', 'Created');
        await dispatch(followCommunity(currentCommunity, user.id, null, true));
      }

      let visitorReplyId = null;
      if (visitorReplyData) {
        const response = await dispatch(respondPost(visitorReplyData));
        visitorReplyId = response.postId;
      }

      setTimeout(() => {
        dispatch(closeModal());
        handleSignupFlow(dispatch, getState, signupContext, user.id, visitorReplyId);
      }, 100);

    } catch (ex) {
      log.error(ex);
      dispatch(setAlert(ex.message));
      return dispatch({ type: actionTypes.SOCIAL_SIGN_UP_FAILURE });
    }
  };
};

export const logout = () => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.LOGOUT });

    try {
      const response = await api.auroraDel('session');
      const { data: { status } } = response;

      if (status && !status.success) {
        dispatch(setAlert(status.message, 'danger', false));
        return dispatch({ type: actionTypes.LOGOUT_FAILURE });
      }

      dispatch({ type: actionTypes.LOGOUT_SUCCESS });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.LOGOUT_FAILURE });
    }
  };
};

export const isUsernameAvailable = (username) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.CHECK_USERNAME });
    try {
      const { data: { payload } } = await api.auroraGet(`profile/check-username?username=${encodeURIComponent(username)}`);
      return dispatch({
        type: actionTypes.CHECK_USERNAME_SUCCESS,
        isAvailable: payload && payload.isAvailable,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.CHECK_USERNAME_FAILURE, isAvailable: false });
    }
  };
};

export const isEmailAvailable = (email) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.CHECK_EMAIL });
    try {
      const { data: { payload } } = await api.auroraGet(`profile/check-email?email=${encodeURIComponent(email)}`);
      return dispatch({
        type: actionTypes.CHECK_EMAIL_SUCCESS,
        isAvailable: payload && payload.isAvailable,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.CHECK_EMAIL_FAILURE, isAvailable: false });
    }
  };
};



// WEBPACK FOOTER //
// ./src/modules/shared/auth/actions.js